/**
 * "Export options…" / "Load options…" — W5.1b.
 *
 * Jeremy Lindsay asked for Redgate/ApexSQL-style comparison profiles,
 * specifically as a text file his team can commit to Git alongside the
 * schema it compares (D4: a file + W5.1c's pin, not a named-profiles
 * manager). The file is self-contained (`compareOptionsFile.ts`) and carries
 * no connection/database identity.
 *
 * Load applies to THIS dialog session only — `onLoad` writes into the Setup
 * dialog's local `options`/`schemaFilter` state, never into the saved
 * per-engine defaults (`setSchemaCompareDefaultOptions`). Loading someone
 * else's file is an experiment; clobbering your own defaults as a side
 * effect of that experiment is exactly the surprise this wave exists to
 * eliminate.
 */

import { useCallback } from 'react';
import { Download, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toaster';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type { SchemaCompareOptions, SchemaFilter } from '../../core/types/workspaceTypes';
import {
  serializeCompareOptionsFile,
  stringifyCompareOptionsFile,
  parseCompareOptionsFile,
  COMPARE_OPTIONS_FILE_EXTENSION,
} from '../utils/compareOptionsFile';
import {
  exportCompareOptionsFileToDisk,
  importCompareOptionsFileFromDisk,
} from '../services/compareOptionsFileApi';

type Props = {
  options: SchemaCompareOptions;
  schemaFilter: SchemaFilter | undefined;
  engine: DatabaseEngineId | undefined;
  /** Applies the loaded values to the current dialog session only. */
  onLoad: (options: SchemaCompareOptions, schemaFilter: SchemaFilter | undefined) => void;
  disabled?: boolean;
};

/**
 * Fix round 1 (IMPORTANT 3) — a foreign-engine profile can carry hundreds or
 * thousands of `ignoredProperties` entries (a hostile or just very-old file),
 * and joining the FULL dropped list into a toast description produced an
 * 80k+-character string in review — unreadable, which is functionally the
 * same defect class as no warning at all. The code still sees every dropped
 * name (`parsed.data.droppedIgnoredProperties`, unabridged) — only the
 * user-facing STRING is capped.
 */
const MAX_DROPPED_NAMES_SHOWN = 5;

function formatDroppedNames(names: string[]): string {
  if (names.length <= MAX_DROPPED_NAMES_SHOWN) return names.join(', ');
  const shown = names.slice(0, MAX_DROPPED_NAMES_SHOWN).join(', ');
  return `${shown}, and ${names.length - MAX_DROPPED_NAMES_SHOWN} more`;
}

export function SchemaCompareOptionsFileButtons({
  options,
  schemaFilter,
  engine,
  onLoad,
  disabled = false,
}: Props) {
  const { track } = useAnalytics();

  const handleExport = useCallback(async () => {
    const json = stringifyCompareOptionsFile(serializeCompareOptionsFile(options, schemaFilter));
    const result = await exportCompareOptionsFileToDisk({
      json,
      suggestedFileName: `schema-compare-options.${COMPARE_OPTIONS_FILE_EXTENSION}`,
    });
    if (!result.success) {
      if (result.cancelled) return;
      toast.error({
        title: 'Could not export comparison options',
        description: result.error ?? 'Something went wrong.',
      });
      return;
    }
    // `AnalyticsEventData` values must all be defined (analyticsTypes.ts) —
    // 'unknown' is the house convention for "genuinely absent", not `undefined`.
    track('feature:schema_compare_options_export', { engine: engine ?? 'unknown' });
    toast.success({
      title: 'Comparison options exported',
      description:
        'Safe to commit to Git — no connection details or database names, only your schema filter and options.',
    });
  }, [options, schemaFilter, engine, track]);

  const handleLoad = useCallback(async () => {
    const result = await importCompareOptionsFileFromDisk();
    if (!result.success) {
      if (result.cancelled) return;
      toast.error({
        title: 'Could not load comparison options',
        description: result.error ?? 'Something went wrong.',
      });
      return;
    }

    const parsed = parseCompareOptionsFile(result.json, engine);
    if (!parsed.success) {
      toast.error({ title: 'Could not load comparison options', description: parsed.error });
      return;
    }

    onLoad(parsed.data.options, parsed.data.schemaFilter);
    // Soporte 9 review round 2 — the engine-gated `ignoreOwner` drop is
    // disclosed through the same toast as dropped properties: a silent drop
    // would let a re-save quietly strip the flag from a shared profile.
    const droppedNames = [
      ...parsed.data.droppedIgnoredProperties,
      ...(parsed.data.droppedIgnoreOwner ? ['Ignore object owner'] : []),
    ];
    const dropped = droppedNames.length > 0;
    track('feature:schema_compare_options_import', { engine: engine ?? 'unknown', dropped });

    if (dropped) {
      toast.warning({
        title: 'Some options from this file were skipped',
        description: `Not used by this engine: ${formatDroppedNames(droppedNames)}.`,
      });
    } else {
      toast.success({
        title: 'Comparison options loaded',
        description: 'Applied to this dialog only — your saved defaults are untouched.',
      });
    }
  }, [engine, onLoad, track]);

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-7 gap-1.5"
        onClick={handleExport}
        disabled={disabled}
        data-testid="schema-compare-export-options-button"
        title="Export the current schema filter, depth, object types, and advanced options as a shareable file."
      >
        <Download className="h-3.5 w-3.5" />
        Export options…
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-7 gap-1.5"
        onClick={handleLoad}
        disabled={disabled}
        data-testid="schema-compare-import-options-button"
        title="Load comparison options from a file — applies to this dialog only, never to your saved defaults."
      >
        <Upload className="h-3.5 w-3.5" />
        Load options…
      </Button>
    </>
  );
}
